<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const loading = ref(true);
const error = ref(false);
const hasLicenseError = ref(false);
const licenseErrorDetails = ref(null);
const dashboard = ref({});
const orders = ref([]);
const orderCount = ref(0);
const selectedProvider = ref('');
const selectedOrder = ref(null);
const selectedPeriod = ref('all');
const customSince = ref('');
const customUntil = ref('');
const providerMenuOpen = ref(false);
const periodMenuOpen = ref(false);
const customPeriodOpen = ref(false);
const syncing = ref(false);
const creatingContact = ref(false);
const copiedCheckout = ref(false);
const copiedContactField = ref('');
const salesProviders = ['mercado_pago', 'asaas', 'kiwify', 'hotmart'];
const providers = computed(() => {
  const integrations = store.getters['integrations/getAppIntegrations'] || [];

  return integrations
    .filter(
      integration =>
        salesProviders.includes(integration.id) &&
        integration.hooks?.some(hook => hook.status)
    )
    .map(integration => integration.id);
});
const providerLabel = computed(() =>
  selectedProvider.value
    ? t(`SALES.PROVIDERS.${selectedProvider.value}`)
    : t('SALES.ALL_PROVIDERS')
);
const periodLabel = computed(() => {
  const labels = {
    all: 'SALES.PERIOD_ALL',
    7: 'SALES.PERIOD_7_DAYS',
    30: 'SALES.PERIOD_30_DAYS',
    90: 'SALES.PERIOD_90_DAYS',
    custom: 'SALES.PERIOD_CUSTOM',
  };
  return t(labels[selectedPeriod.value]);
});

const metricLabels = {
  gross_revenue: 'SALES.GROSS_REVENUE',
  net_revenue: 'SALES.NET_REVENUE',
  paid_count: 'SALES.PAID_COUNT',
  pending_count: 'SALES.PENDING_COUNT',
};

const metrics = computed(() =>
  ['gross_revenue', 'net_revenue', 'paid_count', 'pending_count'].map(key => ({
    key,
    label: t(metricLabels[key]),
    value: dashboard.value[key] ?? 0,
    icon: {
      gross_revenue: 'i-lucide-badge-dollar-sign',
      net_revenue: 'i-lucide-wallet',
      paid_count: 'i-lucide-circle-check',
      pending_count: 'i-lucide-clock-3',
    }[key],
  }))
);

const headers = computed(() => [
  t('SALES.ORDER'),
  t('SALES.BUYER'),
  t('SALES.PROVIDER'),
  t('SALES.STATUS'),
  t('SALES.VALUE'),
  t('SALES.DATE'),
]);

const formatAmount = (amount, currency = 'BRL') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(
    Number(amount || 0)
  );

const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
        new Date(value)
      )
    : '—';

const formatShortDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(value))
    : '';

const reportPeriod = computed(() => {
  const { from, to } = dashboard.value.period || {};
  if (!from || !to) return t('SALES.PERIOD_EMPTY');
  return `${formatDate(from)} – ${formatDate(to)}`;
});

const periodParams = computed(() => {
  if (selectedPeriod.value === 'custom') {
    return {
      ...(customSince.value ? { since: customSince.value } : {}),
      ...(customUntil.value ? { until: `${customUntil.value}T23:59:59` } : {}),
    };
  }
  if (selectedPeriod.value === 'all') return {};
  const since = new Date();
  since.setDate(since.getDate() - Number(selectedPeriod.value));
  return { since: since.toISOString(), until: new Date().toISOString() };
});

const periodBadgeLabel = computed(() => {
  if (selectedPeriod.value === 'all') return '';
  const { since, until } = periodParams.value;
  if (!since || !until) return '';
  return `${formatShortDate(since)} – ${formatShortDate(until)}`;
});

const statusLabel = status =>
  t(`SALES.STATUS_LABELS.${String(status || '').toLowerCase()}`);

const statusColor = status =>
  ({
    paid: 'teal',
    pending: 'amber',
    failed: 'ruby',
    refunded: 'slate',
    chargeback: 'ruby',
    canceled: 'slate',
    overdue: 'amber',
  })[String(status || '').toLowerCase()] || 'slate';

const load = async () => {
  loading.value = true;
  error.value = false;
  try {
    const base = `/api/v1/accounts/${route.params.accountId}/sales`;
    const [summary, list] = await Promise.all([
      window.axios.get(`${base}/dashboard`, {
        params: {
          ...periodParams.value,
          ...(selectedProvider.value
            ? { provider: selectedProvider.value }
            : {}),
        },
      }),
      window.axios.get(`${base}/orders`, {
        params: {
          ...periodParams.value,
          ...(selectedProvider.value
            ? { provider: selectedProvider.value }
            : {}),
        },
      }),
    ]);
    dashboard.value = summary.data;
    orders.value = list.data.orders;
    orderCount.value = list.data.meta.count;
  } catch (exception) {
    const response = exception.response;
    const isLicenseError =
      response?.status === 403 ||
      exception.status === 403 ||
      response?.data?.code === 'STACKLAB_TOKEN_NOT_FOUND';
    if (isLicenseError) {
      hasLicenseError.value = true;
      licenseErrorDetails.value = response?.data || exception.data || exception;
    } else {
      error.value = true;
    }
  } finally {
    loading.value = false;
  }
};

const changeProvider = async () => {
  if (syncing.value) return;
  syncing.value = true;
  const provider = selectedProvider.value || 'all';
  const endpoint = `/api/v1/accounts/${route.params.accountId}/sales/connections/${provider}/sync`;
  try {
    await window.axios
      .post(endpoint, periodParams.value)
      .catch(() => undefined);
    await load();
  } finally {
    syncing.value = false;
  }
};

const selectProvider = provider => {
  selectedProvider.value = provider;
  providerMenuOpen.value = false;
  changeProvider();
};

const selectPeriod = period => {
  if (period === 'custom') {
    selectedPeriod.value = period;
    periodMenuOpen.value = false;
    customPeriodOpen.value = true;
    return;
  }
  if (period) selectedPeriod.value = period;
  periodMenuOpen.value = false;
  load();
};

const applyCustomPeriod = () => {
  customPeriodOpen.value = false;
  load();
};

const openOrder = async order => {
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${route.params.accountId}/sales/orders/${order.id}`
    );
    selectedOrder.value = data;
  } catch {
    selectedOrder.value = order;
  }
};

const createContact = async () => {
  creatingContact.value = true;
  try {
    const { data } = await window.axios.post(
      `/api/v1/accounts/${route.params.accountId}/contacts`,
      {
        name: selectedOrder.value.buyer_name,
        email: selectedOrder.value.buyer_email,
        phone_number: selectedOrder.value.buyer_phone,
      }
    );
    const contact = data.payload || data;
    await window.axios.put(
      `/api/v1/accounts/${route.params.accountId}/sales/orders/${selectedOrder.value.id}`,
      { order: { contact_id: contact.id } }
    );
    selectedOrder.value = { ...selectedOrder.value, contact };
  } finally {
    creatingContact.value = false;
  }
};

const copyCheckoutUrl = async url => {
  await navigator.clipboard.writeText(url);
  copiedCheckout.value = true;
  setTimeout(() => {
    copiedCheckout.value = false;
  }, 2000);
};

const copyContactValue = async (field, value) => {
  await navigator.clipboard.writeText(value);
  copiedContactField.value = field;
  setTimeout(() => {
    copiedContactField.value = '';
  }, 2000);
};

const startConversation = async toggle => {
  const contact = selectedOrder.value.contact;
  store.commit('contacts/SET_CONTACT_ITEM', contact);
  await store.dispatch('contacts/fetchContactableInbox', contact.id);
  toggle();
};

const openContact = () => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId: selectedOrder.value.contact.id,
    },
  });
};

onMounted(async () => {
  await store.dispatch('integrations/get');
  if (providers.value.length === 1) selectedProvider.value = providers.value[0];
  await load();
});
</script>

<template>
  <main
    v-if="!hasLicenseError"
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-2"
  >
    <header class="bg-n-surface-1 px-8 pb-0 pt-6">
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div>
          <p class="mb-1 text-sm text-n-slate-11">{{ t('SALES.OVERVIEW') }}</p>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ t('SALES.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">{{ t('SALES.SUBTITLE') }}</p>
          <p class="mt-2 text-xs text-n-slate-10">
            {{ t('SALES.PERIOD_LABEL', { period: reportPeriod }) }}
          </p>
        </div>
        <div
          v-on-clickaway="
            () => {
              periodMenuOpen = false;
              customPeriodOpen = false;
            }
          "
          class="relative flex flex-wrap items-center justify-end gap-2"
        >
          <div v-on-clickaway="() => (periodMenuOpen = false)" class="relative">
            <button
              type="button"
              class="flex min-w-40 shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="periodMenuOpen = !periodMenuOpen"
            >
              <span class="flex min-w-0 items-center gap-2">
                <span>{{ periodLabel }}</span>
                <span
                  v-if="periodBadgeLabel"
                  class="max-w-40 truncate rounded-md bg-n-slate-3 px-1.5 py-0.5 text-xs text-n-slate-11"
                >
                  {{ periodBadgeLabel }}
                </span>
              </span>
              <span class="i-lucide-chevron-down size-4" />
            </button>
            <div
              v-if="periodMenuOpen"
              class="absolute right-0 z-40 mt-1 w-48 rounded-lg border border-n-weak bg-n-surface-1 p-1 shadow-lg"
            >
              <button
                v-for="option in [
                  ['all', 'SALES.PERIOD_ALL'],
                  ['7', 'SALES.PERIOD_7_DAYS'],
                  ['30', 'SALES.PERIOD_30_DAYS'],
                  ['90', 'SALES.PERIOD_90_DAYS'],
                  ['custom', 'SALES.PERIOD_CUSTOM'],
                ]"
                :key="option[0]"
                type="button"
                class="flex w-full items-center rounded-md px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                @click="selectPeriod(option[0])"
              >
                <span class="flex-1">{{ t(option[1]) }}</span>
                <span
                  v-if="selectedPeriod === option[0]"
                  class="i-lucide-check size-4 text-n-brand"
                />
              </button>
            </div>
            <div
              v-if="customPeriodOpen"
              class="absolute right-0 top-full z-40 mt-1 w-72 rounded-lg border border-n-weak bg-n-surface-1 p-4 shadow-lg"
            >
              <p class="mb-3 text-sm font-medium text-n-slate-12">
                {{ t('SALES.PERIOD_CUSTOM') }}
              </p>
              <div class="flex flex-col gap-3">
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('SALES.PERIOD_FROM') }}
                  <input
                    v-model="customSince"
                    type="date"
                    class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
                  />
                </label>
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('SALES.PERIOD_TO') }}
                  <input
                    v-model="customUntil"
                    type="date"
                    class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
                  />
                </label>
                <Button
                  :label="t('SALES.APPLY_PERIOD')"
                  sm
                  @click="applyCustomPeriod"
                />
              </div>
            </div>
          </div>
          <Button
            :label="t('SALES.REFRESH')"
            icon="i-lucide-refresh-cw"
            slate
            sm
            :is-loading="loading || syncing"
            @click="changeProvider"
          />
          <div
            v-on-clickaway="() => (providerMenuOpen = false)"
            class="relative"
          >
            <button
              type="button"
              class="flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2 text-sm text-n-slate-12"
              :class="
                providers.length > 1
                  ? 'rounded-lg border border-n-weak bg-n-surface-1 hover:bg-n-alpha-2'
                  : 'cursor-default'
              "
              @click="
                providers.length > 1 && (providerMenuOpen = !providerMenuOpen)
              "
            >
              <img
                v-if="selectedProvider"
                :src="`/dashboard/images/integrations/${selectedProvider}.png`"
                :alt="providerLabel"
                class="size-5 rounded object-contain"
              />
              <span v-else class="flex items-center -space-x-1">
                <img
                  v-for="provider in providers"
                  :key="provider"
                  :src="`/dashboard/images/integrations/${provider}.png`"
                  :alt="t(`SALES.PROVIDERS.${provider}`)"
                  class="size-5 rounded-full border border-n-surface-1 bg-n-surface-1 object-contain"
                />
              </span>
              <span class="whitespace-nowrap">{{ providerLabel }}</span>
              <span
                v-if="providers.length > 1"
                class="i-lucide-chevron-down size-4"
              />
            </button>
            <div
              v-if="providerMenuOpen && providers.length > 1"
              class="absolute right-0 z-40 mt-1 w-56 rounded-lg border border-n-weak bg-n-surface-1 p-1 shadow-lg"
            >
              <button
                type="button"
                class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                @click="selectProvider('')"
              >
                <span
                  class="flex size-7 items-center justify-center rounded-md bg-n-slate-3 text-xs font-semibold"
                >
                  {{ t('SALES.ALL') }}
                </span>
                <span class="flex-1">{{ t('SALES.ALL_PROVIDERS') }}</span>
                <span
                  v-if="!selectedProvider"
                  class="i-lucide-check size-4 text-n-brand"
                />
              </button>
              <button
                v-for="provider in providers"
                :key="provider"
                type="button"
                class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                @click="selectProvider(provider)"
              >
                <img
                  :src="`/dashboard/images/integrations/${provider}.png`"
                  :alt="t(`SALES.PROVIDERS.${provider}`)"
                  class="size-7 rounded-md object-contain"
                />
                <span class="flex-1">{{
                  t(`SALES.PROVIDERS.${provider}`)
                }}</span>
                <span
                  v-if="selectedProvider === provider"
                  class="i-lucide-check size-4 text-n-brand"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-8 py-6">
      <Spinner v-if="loading" class="text-n-brand" />
      <div
        v-else-if="error"
        class="rounded-xl border border-n-ruby-8 bg-n-ruby-2 p-6 text-sm text-n-ruby-11"
      >
        {{ t('SALES.ERROR') }}
      </div>
      <template v-else>
        <section
          class="grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
        >
          <article
            v-for="metric in metrics"
            :key="metric.key"
            class="min-w-0 rounded-xl border border-n-weak bg-n-surface-1 p-5"
          >
            <div class="mb-5 flex items-center justify-between">
              <span class="text-sm text-n-slate-11">{{ metric.label }}</span>
              <span
                class="flex size-9 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
              >
                <span :class="metric.icon" class="size-5" />
              </span>
            </div>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{
                ['paid_count', 'pending_count'].includes(metric.key)
                  ? metric.value
                  : formatAmount(metric.value)
              }}
            </p>
          </article>
        </section>

        <section
          class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-surface-1"
        >
          <div
            class="flex items-center justify-between border-b border-n-weak px-6 py-4"
          >
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('SALES.RECENT_ORDERS') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('SALES.RECENT_ORDERS_DESCRIPTION') }}
              </p>
            </div>
            <span
              class="rounded-full bg-n-slate-3 px-3 py-1 text-xs font-medium text-n-slate-11"
            >
              {{ orderCount }}
            </span>
          </div>
          <div class="min-h-0 flex-1 overflow-auto px-6">
            <BaseTable
              :headers="headers"
              :items="orders"
              :no-data-message="t('SALES.EMPTY')"
              sticky-header
            >
              <template #row>
                <BaseTableRow
                  v-for="order in orders"
                  :key="order.id"
                  :item="order"
                  class="cursor-pointer hover:bg-n-alpha-2"
                  @click="openOrder(order)"
                >
                  <template #default>
                    <td class="py-4 pr-4 text-sm font-medium text-n-slate-12">
                      {{ order.external_id }}
                    </td>
                    <td class="py-4 pr-4 text-sm text-n-slate-11">
                      <div v-if="order.contact" class="flex items-center gap-2">
                        <Avatar
                          :name="order.contact.name || order.buyer_name"
                          :src="order.contact.thumbnail"
                          :size="32"
                        />
                        <div class="min-w-0">
                          <div class="truncate font-medium text-n-slate-12">
                            {{ order.contact.name || order.buyer_name || '—' }}
                          </div>
                          <Label
                            :label="t('SALES.REGISTERED')"
                            color="teal"
                            compact
                          />
                          <div class="mt-1 text-xs text-n-slate-10">
                            <div
                              v-if="order.buyer_email || order.contact.email"
                            >
                              {{ order.buyer_email || order.contact.email }}
                            </div>
                            <div
                              v-if="
                                order.buyer_phone || order.contact.phone_number
                              "
                            >
                              {{
                                order.buyer_phone || order.contact.phone_number
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="min-w-0">
                        <div class="truncate text-n-slate-12">
                          {{ order.buyer_name || t('SALES.UNKNOWN_BUYER') }}
                        </div>
                        <div class="truncate text-xs text-n-slate-10">
                          {{ order.buyer_email || order.buyer_phone || '—' }}
                        </div>
                      </div>
                    </td>
                    <td class="py-4 pr-4 text-sm text-n-slate-11">
                      <div class="flex items-center gap-2">
                        <img
                          :src="`/dashboard/images/integrations/${order.provider}.png`"
                          :alt="t(`SALES.PROVIDERS.${order.provider}`)"
                          class="size-5 rounded object-contain"
                        />
                        <span class="whitespace-nowrap">
                          {{ t(`SALES.PROVIDERS.${order.provider}`) }}
                        </span>
                      </div>
                    </td>
                    <td class="py-4 pr-4 text-sm text-n-slate-11">
                      <Label
                        :label="statusLabel(order.status)"
                        :color="statusColor(order.status)"
                        compact
                      />
                    </td>
                    <td class="py-4 pr-4 text-sm text-n-slate-12">
                      {{ formatAmount(order.gross_amount, order.currency) }}
                    </td>
                    <td class="py-4 text-sm text-n-slate-11">
                      {{
                        formatDate(
                          order.provider_created_at || order.created_at
                        )
                      }}
                    </td>
                  </template>
                </BaseTableRow>
              </template>
            </BaseTable>
          </div>
        </section>
      </template>
    </div>
  </main>

  <div
    v-if="hasLicenseError"
    class="flex h-full w-full items-center justify-center bg-n-surface-2"
  >
    <div class="mx-auto max-w-md p-6 text-center">
      <span class="i-lucide-lock mb-4 inline-block size-12 text-n-slate-10" />
      <h2 class="mb-2 text-xl font-semibold text-n-slate-12">
        {{ t('SALES.LICENSE_REQUIRED') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('SALES.LICENSE_DESCRIPTION') }}
      </p>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="selectedOrder" class="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        class="absolute inset-0 cursor-default bg-n-slate-12/20"
        :aria-label="t('SALES.CLOSE_DETAILS')"
        @click="selectedOrder = null"
      />
      <aside
        class="relative flex h-full w-full max-w-[28rem] flex-col border-l border-n-weak bg-n-surface-1 shadow-xl"
      >
        <header class="flex items-center justify-between px-6 py-4">
          <div>
            <p class="text-xs text-n-slate-11">
              {{ t('SALES.ORDER_DETAILS') }}
            </p>
            <h2 class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ selectedOrder.external_id }}
            </h2>
          </div>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="t('SALES.CLOSE_DETAILS')"
            @click="selectedOrder = null"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto p-6">
          <section class="mb-5">
            <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
              {{ t('SALES.SALE_INFORMATION') }}
            </h3>
            <dl class="divide-y divide-n-weak rounded-xl border border-n-weak">
              <div class="flex justify-between gap-4 px-4 py-3">
                <dt class="text-sm text-n-slate-11">
                  {{ t('SALES.PROVIDER') }}
                </dt>
                <dd class="text-sm font-medium text-n-slate-12">
                  {{ selectedOrder.provider }}
                </dd>
              </div>
              <div class="flex justify-between gap-4 px-4 py-3">
                <dt class="text-sm text-n-slate-11">{{ t('SALES.STATUS') }}</dt>
                <dd>
                  <Label
                    :label="statusLabel(selectedOrder.status)"
                    :color="statusColor(selectedOrder.status)"
                    compact
                  />
                </dd>
              </div>
              <div class="flex justify-between gap-4 px-4 py-3">
                <dt class="text-sm text-n-slate-11">{{ t('SALES.VALUE') }}</dt>
                <dd class="text-sm font-medium text-n-slate-12">
                  {{
                    formatAmount(
                      selectedOrder.gross_amount,
                      selectedOrder.currency
                    )
                  }}
                </dd>
              </div>
              <div
                v-if="selectedOrder.checkout_url"
                class="flex flex-col gap-2 px-4 py-3"
              >
                <dt class="text-sm text-n-slate-11">
                  {{ t('SALES.CHECKOUT_LINK') }}
                </dt>
                <dd class="flex min-w-0 items-center gap-2">
                  <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
                    {{ selectedOrder.checkout_url }}
                  </span>
                  <button
                    type="button"
                    class="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-n-brand hover:bg-n-alpha-2"
                    @click="copyCheckoutUrl(selectedOrder.checkout_url)"
                  >
                    <span
                      :class="
                        copiedCheckout ? 'i-lucide-check' : 'i-lucide-copy'
                      "
                      class="size-3.5"
                    />
                    {{
                      copiedCheckout ? t('SALES.COPIED') : t('SALES.COPY_LINK')
                    }}
                  </button>
                </dd>
              </div>
            </dl>
          </section>
          <section>
            <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
              {{ t('SALES.CONTACT_INFORMATION') }}
            </h3>
            <div class="rounded-xl border border-n-weak p-4">
              <div class="flex items-center gap-3">
                <Avatar
                  :name="
                    selectedOrder.contact?.name ||
                    selectedOrder.buyer_name ||
                    t('SALES.UNKNOWN_BUYER')
                  "
                  :src="selectedOrder.contact?.thumbnail"
                  :size="48"
                />
                <div class="min-w-0">
                  <div class="flex items-center gap-1">
                    <div class="truncate font-medium text-n-slate-12">
                      {{
                        selectedOrder.contact?.name ||
                        selectedOrder.buyer_name ||
                        t('SALES.UNKNOWN_BUYER')
                      }}
                    </div>
                    <button
                      v-if="selectedOrder.contact"
                      type="button"
                      class="shrink-0 rounded-md px-1.5 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                      :aria-label="t('SALES.VIEW_CONTACT')"
                      @click="openContact"
                    >
                      {{ t('SALES.VIEW_CONTACT') }}
                    </button>
                  </div>
                  <Label
                    :label="
                      selectedOrder.contact
                        ? t('SALES.REGISTERED')
                        : t('SALES.NOT_REGISTERED')
                    "
                    :color="selectedOrder.contact ? 'teal' : 'amber'"
                    compact
                  />
                </div>
              </div>
              <div class="mt-3 space-y-1 text-sm text-n-slate-11">
                <button
                  v-if="selectedOrder.buyer_email"
                  type="button"
                  class="block text-left hover:text-n-slate-12"
                  :class="{
                    'text-n-teal-11': copiedContactField === 'email',
                  }"
                  @click="copyContactValue('email', selectedOrder.buyer_email)"
                >
                  {{
                    copiedContactField === 'email'
                      ? t('SALES.COPIED')
                      : selectedOrder.buyer_email
                  }}
                </button>
                <button
                  v-if="selectedOrder.buyer_phone"
                  type="button"
                  class="block text-left hover:text-n-slate-12"
                  :class="{
                    'text-n-teal-11': copiedContactField === 'phone',
                  }"
                  @click="copyContactValue('phone', selectedOrder.buyer_phone)"
                >
                  {{
                    copiedContactField === 'phone'
                      ? t('SALES.COPIED')
                      : selectedOrder.buyer_phone
                  }}
                </button>
              </div>
              <div class="mt-4 flex flex-wrap gap-2">
                <Button
                  v-if="!selectedOrder.contact"
                  :label="t('SALES.CREATE_CONTACT')"
                  icon="i-lucide-user-plus"
                  sm
                  :is-loading="creatingContact"
                  @click="createContact"
                />
                <ComposeConversation
                  v-if="selectedOrder.contact"
                  :contact-id="String(selectedOrder.contact.id)"
                  is-modal
                >
                  <template #trigger="{ toggle }">
                    <Button
                      :label="t('SALES.START_CONVERSATION')"
                      icon="i-lucide-message-circle"
                      slate
                      sm
                      @click="startConversation(toggle)"
                    />
                  </template>
                </ComposeConversation>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  </Teleport>
</template>
